import React from "react";
import { useParams } from "react-router-dom";
import { Heart, Shield, Users, Sparkles, Apple, Smartphone } from "lucide-react";

const PromoLandingPage = () => {
  const { code } = useParams();
  const promoCode = code?.toUpperCase() || "LAUNCH";
  
  // Map codes to their Apple redemption URLs
  const appleRedemptionUrls = {
    "LAUNCH": "https://apps.apple.com/redeem?ctx=offercodes&id=6757411835&code=LAUNCH",
    "PHILLYNA": "https://apps.apple.com/redeem?ctx=offercodes&id=6757411835&code=PHILLYNA",
    "CPRANA": "https://apps.apple.com/redeem?ctx=offercodes&id=6757411835&code=CPRANA",
    "ELITE": "https://apps.apple.com/redeem?ctx=offercodes&id=6757411835&code=ELITE",
    "PHILLYELITE": "https://apps.apple.com/redeem?ctx=offercodes&id=6757411835&code=PHILLYELITE",
    "CPRAELITE": "https://apps.apple.com/redeem?ctx=offercodes&id=6757411835&code=CPRAELITE",
  };

  const appleUrl = appleRedemptionUrls[promoCode] || appleRedemptionUrls["LAUNCH"];
  const androidUrl = "https://play.google.com/store/apps/details?id=com.recoverymatch.dating";

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C3A7A] to-[#0f2452]">
      {/* Header */}
      <header className="pt-8 pb-4 px-4 text-center">
        <img 
          src="/images/logo-main.png" 
          alt="RecoveryMatch" 
          className="w-20 h-20 mx-auto rounded-2xl shadow-lg"
        />
        <h1 className="text-3xl font-bold text-white mt-4">RecoveryMatch</h1>
        <p className="text-[#FAB710] text-lg font-medium mt-1">Clean/Sober Dating, Real Connections.</p>
      </header>

      {/* Promo Banner */}
      <div className="mx-4 mt-6 bg-[#FAB710] rounded-2xl p-6 text-center shadow-xl">
        <p className="text-[#1C3A7A] text-sm font-semibold uppercase tracking-wide">Special Offer</p>
        <h2 className="text-[#1C3A7A] text-4xl font-bold mt-2">1 Month FREE!</h2>
        <p className="text-[#1C3A7A]/80 mt-2">Get full access to Recovery+ features</p>
        <div className="mt-4 bg-white/30 rounded-xl py-3 px-6 inline-block">
          <p className="text-[#1C3A7A] text-sm">Your Code:</p>
          <p className="text-[#1C3A7A] text-2xl font-bold tracking-wider">{promoCode}</p>
        </div>
      </div>

      {/* Why RecoveryMatch */}
      <div className="mx-4 mt-8 bg-white/10 backdrop-blur rounded-2xl p-6">
        <h3 className="text-white text-xl font-bold text-center mb-6">Why RecoveryMatch?</h3>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#FAB710] rounded-xl flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-[#1C3A7A]" />
            </div>
            <div>
              <p className="text-white font-semibold">Everyone Gets It</p>
              <p className="text-white/70 text-sm">No explaining your recovery story</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#FAB710] rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-[#1C3A7A]" />
            </div>
            <div>
              <p className="text-white font-semibold">Safe & Judgment-Free</p>
              <p className="text-white/70 text-sm">Built-in safety features & moderation</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#FAB710] rounded-xl flex items-center justify-center flex-shrink-0">
              <Heart className="w-6 h-6 text-[#1C3A7A]" />
            </div>
            <div>
              <p className="text-white font-semibold">Real Connections</p>
              <p className="text-white/70 text-sm">Meet people who share your values</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#FAB710] rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-[#1C3A7A]" />
            </div>
            <div>
              <p className="text-white font-semibold">AI-Powered Features</p>
              <p className="text-white/70 text-sm">Bio assistant & conversation starters</p>
            </div>
          </div>
        </div>
      </div>

      {/* Download Section */}
      <div className="mx-4 mt-8 mb-8">
        <h3 className="text-white text-xl font-bold text-center mb-6">Get Your Free Month!</h3>
        
        {/* iOS Button */}
        <a 
          href={appleUrl}
          className="block w-full bg-white rounded-2xl p-5 mb-4 shadow-lg hover:scale-[1.02] transition-transform"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-black rounded-xl flex items-center justify-center">
              <Apple className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-sm">iPhone User?</p>
              <p className="text-[#1C3A7A] text-xl font-bold">Download for iOS</p>
              <p className="text-gray-500 text-sm">Tap to redeem your free month</p>
            </div>
          </div>
        </a>

        {/* Android Button */}
        <a 
          href={androidUrl}
          className="block w-full bg-white rounded-2xl p-5 shadow-lg hover:scale-[1.02] transition-transform"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#3DDC84] rounded-xl flex items-center justify-center">
              <Smartphone className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-sm">Android User?</p>
              <p className="text-[#1C3A7A] text-xl font-bold">Download for Android</p>
              <p className="text-gray-500 text-sm">Use code <span className="font-bold">{promoCode}</span> in the app</p>
            </div>
          </div>
        </a>
      </div>

      {/* Instructions */}
      <div className="mx-4 mb-8 bg-white/10 backdrop-blur rounded-2xl p-6">
        <h3 className="text-white text-lg font-bold mb-4">How to Redeem</h3>
        
        <div className="mb-6">
          <p className="text-[#FAB710] font-semibold mb-2 flex items-center gap-2">
            <Apple className="w-4 h-4" /> iPhone Instructions:
          </p>
          <ol className="text-white/80 text-sm space-y-1 ml-6 list-decimal">
            <li>Tap the iOS button above</li>
            <li>Sign in with your Apple ID</li>
            <li>Confirm the free month offer</li>
            <li>Download RecoveryMatch</li>
          </ol>
        </div>
        
        <div>
          <p className="text-[#3DDC84] font-semibold mb-2 flex items-center gap-2">
            <Smartphone className="w-4 h-4" /> Android Instructions:
          </p>
          <ol className="text-white/80 text-sm space-y-1 ml-6 list-decimal">
            <li>Tap the Android button above</li>
            <li>Install RecoveryMatch from Play Store</li>
            <li>Create your account</li>
            <li>Go to Settings → Redeem Code</li>
            <li>Enter: <span className="font-bold text-white">{promoCode}</span></li>
          </ol>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-center pb-8 px-4">
        <p className="text-white/60 text-sm">
          "We get it. We're in recovery too."
        </p>
        <p className="text-white/40 text-xs mt-4">
          recoverymatch.com
        </p>
      </footer>
    </div>
  );
};

export default PromoLandingPage;
